import { datadogRum } from '@datadog/browser-rum';
import React from 'react';
import { toast } from 'react-toastify';

import { Alert } from '../components/atoms';
import { HttpCodes } from '../enums/httpCodes';
import { EventsService } from '../services/events.service';
import { useDownload } from './useDownload';

export const usePrintableEvidence = () => {
  const { isDownloading, checkIfFileExistsAndDownload, checkIfFileExistsAndPrint } = useDownload();

  const handleEvidenceError = async (err: any) => {
    if (err?.status === HttpCodes.CONFLICT) {
      let body: any;
      try {
        body = await err.json();
      } catch {
        toast.error('Reissuance violation: evidence already exists for this plate.');

        return;
      }

      await new Alert(
        <span>
          An evidence PDF was already generated for this license plate at this location.{' '}
          <a href={`/portalList?previewEventID=${body.conflicting_event_id}`} target="_blank" rel="noreferrer">
            View conflicting event #{body.conflicting_event_id} ↗
          </a>
        </span>,
        'OK',
      ).promise;
    } else {
      let message = 'Failed to generate evidence. Please try again.';
      try {
        const body = typeof err?.json === 'function' ? await err.json() : err;
        if (body?.message) {
          message = `Failed to generate evidence: ${body.message}`;
        }
      } catch {
        // Use default message
      }
      toast.error(message);
    }
  };

  const generateEvidence = async (eventId: number, mode: 'print' | 'download') => {
    const start = performance.now();

    let evidence;
    try {
      evidence = await EventsService.generatePrintableEvidence(eventId);
    } catch (err: any) {
      await handleEvidenceError(err);

      return;
    }

    if (!evidence || typeof evidence !== 'object' || typeof evidence.url !== 'string' || !evidence.url) {
      toast.error('Failed to generate evidence: unexpected response from server.');

      return;
    }

    const getUrl = evidence.url;

    const succeeded = mode === 'print'
      ? await checkIfFileExistsAndPrint(getUrl, `${eventId}.pdf`, false, 3000)
      : await checkIfFileExistsAndDownload(getUrl, `${eventId}.pdf`, false, 3000);

    if (succeeded) {
      datadogRum.addTiming('evidenceGeneration', performance.now() - start);
    }
  };

  const printEvidence = (eventId: number) => generateEvidence(eventId, 'print');
  const downloadEvidence = (eventId: number) => generateEvidence(eventId, 'download');

  return { isDownloading, generateEvidence, printEvidence, downloadEvidence };
};
